// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { omit } from 'lodash';
import React from 'react';
import Notification from '../Notification';
import FieldsTable from './FieldsTable';
import getBottlebooksFieldsMap from './getBottlebooksFieldsMap';

const EMPTY = '(empty)';

/**
 * Renders the page where fields are mapped
 * Bottlebooks fields are rendered and all columns from import are available in the dropdown
 * @param {Object} props
 * @param {boolean} props.loading - true if data is loading
 * @param {Object[]} [props.latestBottlebooksFields] - the previous fields mapped to be used on new imports
 * @param {Object[]} props.fieldsToImport - the columns definition
 * @param {import('../Notification').Notification} [props.notification]
 * @param {function} props.cancelNotification
 * @param {object} props.bottlebooksFieldsMap
 * @param {{name:string, sampleRows: string[]}[]} props.sourceFields
 * @param {function} props.handleFieldsTableChange
 */
export default function MapFields({
  loading,
  fieldsToImport,
  notification,
  cancelNotification,
  bottlebooksFieldsMap,
  sourceFields,
  handleFieldsTableChange,
  ...rest
}) {
  if (loading) return null;

  return (
    <React.Fragment>
      <FieldsTable
        sx={{ width: '100%', margin: '0 auto', marginBottom: 5, marginTop: 2 }}
        bottlebooksFieldsMap={bottlebooksFieldsMap}
        sourceFields={sourceFields}
        fieldsToImport={fieldsToImport}
        onChange={(value, col) =>
          handleFieldsTableChange(value, col, bottlebooksFieldsMap)
        }
        {...rest}
      />

      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 * @param {string} props.importType
 * @param {object[]} props.fieldsToImport
 * @returns
 */
export function useMapFields({
  workspaceId,
  importId,
  fieldsToImport,
  importType,
}) {
  const { loading, /* error, */ data /*, refetch */ } = useQuery(QUERY, {
    variables: {
      workspaceId,
      importId,
      importType,
    },
  });

  const afterQueryData = afterQuery({ data, fieldsToImport });

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  /** @type {[boolean, (boolean)=>void]} */
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [_updateColumnsMapping] = useMutation(UPDATE_COLUMNS_MAPPING);

  const [bottlebooksFieldsMap, setBottlebooksFieldsMap] = React.useState(
    afterQueryData.originalBottlebooksFieldsMap
  );
  async function updateColumnsMapping() {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const { data } = await _updateColumnsMapping({
        variables: {
          input: {
            importId,
            bottlebooksFields: Object.values(
              bottlebooksFieldsMap ||
                afterQueryData.originalBottlebooksFieldsMap ||
                {}
            ),
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }

  function areAllRequiredFieldsFilled(bottlebooksFieldsMap) {
    if (!bottlebooksFieldsMap) return null;
    return !fieldsToImport.find((col) => {
      return (
        col.required &&
        (!bottlebooksFieldsMap[col.key] ||
          bottlebooksFieldsMap[col.key] === EMPTY)
      );
    });
  }

  async function handleFieldsTableChange(
    value,
    col,
    originalBottlebooksFieldsMap
  ) {
    let newMapping = {};
    const bottlebooksFieldsMapSrc = bottlebooksFieldsMap
      ? bottlebooksFieldsMap
      : originalBottlebooksFieldsMap;
    if (value === EMPTY) {
      newMapping = omit(bottlebooksFieldsMapSrc, col.key);
      setBottlebooksFieldsMap(newMapping);
    } else {
      newMapping = {
        ...bottlebooksFieldsMapSrc,
        [col.key]: {
          importFieldName: value,
          bottlebooksFieldName: col.key,
        },
      };
      setBottlebooksFieldsMap(newMapping);
    }
  }

  return {
    loading,
    importId,
    ...afterQueryData,
    fieldsToImport,
    updateColumnsMapping,
    isValid:
      areAllRequiredFieldsFilled(
        bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap
      ) && !isSubmitting,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    bottlebooksFieldsMap:
      bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap,
    handleFieldsTableChange,
  };
}

function afterQuery({ data, fieldsToImport }) {
  if (!data) return {};

  // TODO we don't need all the imports. Add filter and limit latest 10 or 20
  const bottlebooksFieldsNodes = data?.workspace?.dataImports?.nodes?.filter(
    ({ bottlebooksFields }) => bottlebooksFields
  );
  const latestBottlebooksFields = bottlebooksFieldsNodes?.reduce(
    (acc, { bottlebooksFields }) => {
      acc = [...bottlebooksFields, ...acc];
      return acc;
    },
    []
  );

  const dataImport = data?.workspace?.dataImport;

  const { source, bottlebooksFields } = dataImport;
  const sourceFieldNames = source?.fields?.map(({ name }) => name);
  const filteredLatestBottlebooksFields = latestBottlebooksFields?.filter(
    ({ importFieldName }) => sourceFieldNames?.includes(importFieldName)
  );

  //The columns automatically matched or matched by the user
  const originalBottlebooksFieldsMap = getBottlebooksFieldsMap({
    bottlebooksFields,
    latestBottlebooksFields: filteredLatestBottlebooksFields,
    sourceFields: source?.fields,
    fieldsToImport,
  });

  return {
    latestBottlebooksFields,
    originalBottlebooksFieldsMap,
    sourceFieldNames,
    sourceFields: source?.fields.map(({ name, rawTexts }) => ({
      name,
      sampleRows: rawTexts,
    })),
  };
}

MapFields.fragment = gql`
  fragment MapFields on DataImport {
    importId
    bottlebooksFields {
      bottlebooksFieldName
      importFieldName
    }
  }

  fragment MapFieldsSource on DataImport {
    source {
      fields {
        name
        bottlebooksFieldName
        rawTexts(first: 3)
      }
    }
  }
`;

const QUERY = gql`
  query MapFieldsPage($workspaceId: ID!, $importId: ID!, $importType: String!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        ...MapFields
        ...MapFieldsSource
      }
      # We only want to import mappings from the same type of imports
      dataImports(filter: { type: { eq: $importType } }) {
        nodes {
          ...MapFields
        }
      }
    }
  }
  ${MapFields.fragment}
`;

export const UPDATE_COLUMNS_MAPPING = gql`
  mutation updateDataImportBottlebooksFields(
    $input: UpdateDataImportBottlebooksFieldsInput!
  ) {
    result: updateDataImportBottlebooksFields(input: $input) {
      code
      success
      message
      dataImport {
        ...MapFields
        ...MapFieldsSource
      }
    }
  }
  ${MapFields.fragment}
`;
